import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    ButtonToolbar,
    Form,
    FormInstance,
    Message,
    Schema,
    SelectPicker,
    toaster,
} from "rsuite";
import ModalContainer from "components/Common/Modal/ModalContainer";
import constants from "utils/constants";
import { addPartner, loadPartnerGroups } from "api/partners/partners-api";
import { IPartner, INewPartner } from "store/partners/partners-types";
import { getErrorMessage } from "api/defaults";
import "./AddNewPartner.less";

const defaultFormValue = {
    partnerName: "",
    aadGroup: {
        id: "",
        displayName: "",
    },
};

interface ComponentProps {
    isOpened: boolean;
    existingPartners: IPartner[];
    setIsOpened: (isOpened: boolean) => void;
    refreshPartners: () => void;
    setModalLoaderVisibility: (visible: boolean) => void;
}

const AddNewPartner: React.FC<ComponentProps> = ({
    isOpened,
    existingPartners,
    setIsOpened,
    refreshPartners,
    setModalLoaderVisibility,
}: ComponentProps) => {
    const { t } = useTranslation();
    const formRef = useRef<FormInstance<Record<string, any>> | null>(null);
    const [partnerName, setPartnerName] = useState<string>("");
    const [aadGroupName, setAadGroupName] = useState<string>("");
    const [partnerNames, setPartnerNames] = useState<string[]>([]);
    const [existingPartnerGroups, setExistingPartnerGroups] = useState<
        string[]
    >([]);
    const [formValue, setFormValue] = useState(defaultFormValue);
    const [partnerGroups, setPartnerGroups] = useState<Record<string, any>[]>(
        []
    );
    const [isGroupsFetched, setIsGroupFetched] = useState(false);

    const fetchPartnerGroups = () => {
        setModalLoaderVisibility(true);
        setIsGroupFetched(false);
        loadPartnerGroups().then(([payload, status]) => {
            setModalLoaderVisibility(false);
            setIsGroupFetched(true);
            if (payload && status === constants.statusCode.OK) {
                const groups = payload.map((value) => {
                    return {
                        label: value.displayName,
                        value: value,
                    };
                });
                setPartnerGroups(groups);
            } else {
                const errorMessage = getErrorMessage(String(payload), status);
                toaster.push(
                    <Message showIcon type="error" closable>
                        {errorMessage}
                    </Message>
                );
            }
        });
    };

    useEffect(() => {
        if (!isGroupsFetched && isOpened) {
            fetchPartnerGroups();
        }
    }, [isGroupsFetched, setIsGroupFetched, fetchPartnerGroups]);

    const handleAddPartner = () => {
        if (!formRef.current?.check()) {
            return;
        }
        setModalLoaderVisibility(true);
        const finalName = partnerName.trim();
        const newPartner: INewPartner = {
            name: finalName,
            aadGroupName: aadGroupName,
        };

        addPartner(newPartner).then(([result, status]) => {
            if (status === constants.statusCode.NoContent) {
                handleCloseModal();
                refreshPartners();
            } else {
                const errorMessage = getErrorMessage(String(result), status);
                toaster.push(
                    <Message showIcon type="error" closable>
                        {errorMessage}
                    </Message>
                );
            }

            setModalLoaderVisibility(false);
        });
    };

    useEffect(() => {
        if (existingPartners) {
            setPartnerNames(existingPartners.map((value) => value.name));
            setExistingPartnerGroups(
                existingPartners.map((value) => value.aadGroupName)
            );
        }
    }, [existingPartners]);

    const handleChangeForm = (value: any) => {
        setPartnerName(value.partnerName);
        setAadGroupName(value.aadGroup.displayName);
        setFormValue(value);
    };

    const handleCloseModal = () => {
        setFormValue(defaultFormValue);
        setIsOpened(false);
        setIsGroupFetched(false);
    };

    const formKey = {
        partnerName: "partnerName",
        aadGroup: "aadGroup",
    };

    const model = Schema.Model({
        partnerName: Schema.Types.StringType()
            .isRequired(t("generalValidationMessages.isRequired"))
            .minLength(
                constants.minNameLengthLimit,
                t("generalValidationMessages.minLength")
            )
            .maxLength(
                constants.maxPartnerNameLengthLimit,
                t("generalValidationMessages.maxLength500")
            )
            .addRule((enteredValue: string) => {
                return partnerNames.indexOf(enteredValue.trim()) < 0;
            }, t("generalValidationMessages.isUnique"))
            .addRule((enteredValue: string) => {
                const final = enteredValue.trim();

                return final.length >= constants.minNameLengthLimit;
            }, t("generalValidationMessages.minLength")),
        aadGroup: Schema.Types.ObjectType()
            .isRequired(t("generalValidationMessages.isRequired"))
            .addRule((enteredValue: any) => {
                return (
                    existingPartnerGroups.indexOf(
                        enteredValue.displayName.trim()
                    ) < 0
                );
            }, t("partners.newPartnerDialog.aadGroupValidation")),
    });

    const header = <h1>{t("partners.newPartnerDialog.title")}</h1>;

    const footer = (
        <ButtonToolbar className="custom-modal__button-toolbar">
            <Button
                type="submit"
                appearance="primary"
                disabled={!isGroupsFetched}
                onClick={handleAddPartner}
            >
                {t("partners.newPartnerDialog.create")}
            </Button>
            <Button
                appearance="ghost"
                disabled={!isGroupsFetched}
                onClick={handleCloseModal}
            >
                {t("partners.newPartnerDialog.cancel")}
            </Button>
        </ButtonToolbar>
    );

    return (
        <ModalContainer
            open={isOpened}
            onClose={handleCloseModal}
            header={header}
            footer={footer}
            className="add-modal__container"
            backdrop="static"
        >
            <>
                <Form
                    className="add-modal__form"
                    ref={formRef}
                    model={model}
                    formValue={formValue}
                    onChange={handleChangeForm}
                >
                    <Form.Group className="add-modal__form-group">
                        <Form.ControlLabel>
                            {t("partners.newPartnerDialog.nameTitle")}
                        </Form.ControlLabel>
                        <Form.Control
                            placeholder={t(
                                "partners.newPartnerDialog.namePlaceholder"
                            )}
                            name={formKey.partnerName}
                        />
                    </Form.Group>
                    <Form.Group className="add-modal__form-group">
                        <Form.ControlLabel>
                            {t("partners.newPartnerDialog.aadGroup")}
                        </Form.ControlLabel>
                        <Form.Control
                            accepter={SelectPicker}
                            data={partnerGroups}
                            virtualized={false}
                            cleanable={false}
                            searchable={false}
                            placeholder={t(
                                "partners.newPartnerDialog.aadGroupPlaceholder"
                            )}
                            name={formKey.aadGroup}
                            className={"tablePicker"}
                            disabled={!isGroupsFetched}
                        />
                    </Form.Group>
                </Form>
            </>
        </ModalContainer>
    );
};

export default AddNewPartner;
